<template>
	<div class="invite-wrapper" :class="{ loading }">
		<div class="login-heading">
			<a href="https://pronto.io">
				<ProntoNameLogoIcon class="name-logo" />
			</a>
		</div>

		<div class="loading-container" v-if="loading">
			<IosSpinner />
		</div>

		<div v-if="!loading && !hasError" class="form-wrapper" :class="{ loading }">
			<div
				v-if="showBackArrow"
				class="back-button"
				@click="back"
				tabindex="0"
				@keyup.enter="back"
			>
				<LeftArrowIcon />
			</div>

			<router-view v-if="!loading" />
		</div>

		<div v-if="!loading && hasError" class="error-container">
			<CircleExclamationIcon type="regular" halo />
			<h2 v-if="error" v-text="error" />
		</div>
	</div>
</template>

<script>
import {
	ProntoNameLogoIcon,
	LeftArrowIcon,
	CircleExclamationIcon,
} from '@/icons'
import store from '@/store'
import {
	fetchInviteMeta,
	inviteRoute,
	getStartedRoute,
	mobileDownloadRoute,
	signinRoute,
	getInvite,
	clearInviteData,
	activeAccountsRoute,
	groupFullRoute,
} from './index'
import IosSpinner from '@/components/IosSpinner'
import { inviteAccessTypes as accessTypes } from '@/helpers/constants'
import { isMobileDevice } from '@/store/browser'
import { redirectToAccount } from '@/helperMethods'

export default {
	components: {
		ProntoNameLogoIcon,
		LeftArrowIcon,
		IosSpinner,
		CircleExclamationIcon,
	},

	data() {
		return {
			loading: true,
			error: null,
		}
	},

	async created() {
		await this.checkInviteLink()
	},

	computed: {
		showBackArrow() {
			if (this.loading) return false
			if (this.hasError) return true
			const hideForRoutes = [
				getStartedRoute,
				signinRoute,
				groupFullRoute,
				mobileDownloadRoute,
				activeAccountsRoute,
			]
			if (hideForRoutes.some((r) => r.name === this.$route.name)) {
				return false
			}
			return true
		},
		hasError() {
			return !!this.error
		},
	},

	watch: {
		'$route.params.code': {
			async handler() {
				await this.checkInviteLink()
			}
		}
	},

	methods: {
		back() {
			if (this.hasError) {
				this.error = null
				return
			}
			this.$router.go(-1)
		},

		async checkInviteLink() {
			const { code } = this.$route.params
			if (!code) {
				this.loading = false
				return
			}

			if (await this.hasCachedInvite(code)) return

			this.invite = await this.fetchInvite(code)

			if (this.isMobile()) return

			if (this.hasActiveAccountsForOrg()) return

			this.loading = false
			this.jumpToStartingScreen()
		},

		async hasCachedInvite(code) {
			const cached = getInvite()
			if (!cached?.code || cached.code !== code) {
				clearInviteData()
				return false
			}
			// we already cached this invite data, no need to refetch
			this.loading = false
			this.invite = cached

			if (this.isMobile()) return true

			if (this.hasActiveAccountsForOrg()) return true

			this.jumpToStartingScreen()
			return true
		},

		hasActiveAccountsForOrg() {
			const { id } = this.invite?.organization || {}
			const accountsForOrg = store
				.getActiveUsers()
				.filter((a) => a.organization?.id === id)
			// no active accounts for this org
			if (!accountsForOrg.length) return false
			// only one active account for this org
			if (accountsForOrg.length === 1) {
				redirectToAccount(accountsForOrg[0], null, { invite: this.invite.code })
				return true
			}
			// multiple active accounts for this org
			this.$router.push({
				name: 'activeAccounts',
				params: this.$route.params,
			})
			return true
		},

		isMobile() {
			if (isMobileDevice) {
				this.$router.push({
					name: mobileDownloadRoute.name,
					params: this.$route.params,
				})
				return true
			}
			return false
		},

		jumpToStartingScreen() {
			if (!this.invite) return
			if (this.$route.name !== inviteRoute.name) return

			if (this.invite.group?.has_memberships_available === false) {
				this.$router.replace({
					name: 'groupFull',
					params: this.$route.params,
				})
				return
			}

			if (this.invite.access === accessTypes.INTERNAL) {
				// if link is internal, force user to sign in before continuing
				this.$router.push({
					name: signinRoute.name,
					params: this.$route.params,
				})
				return
			}

			this.$router.push({
				name: getStartedRoute.name,
				params: this.$route.params,
			})
		},

		async fetchInvite(code) {
			if (!code) return null
			if (this.invite?.code === code) return this.invite
			this.loading = true
			try {
				const invite = await fetchInviteMeta(code)
				this.loading = false
				return invite
			} catch (e) {
				console.error(e)
				this.error =
					e.response?.data?.message ?? this.$t('apierrors_error_generic')
				this.loading = false
				return null
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.invite-wrapper {
	height: 100vh;
	padding: 80px 16px 12px;
	background-color: var(--teal-300);
	display: flex;
	flex-direction: column;
	align-items: center;
	overflow-y: auto;

	@media screen and (max-width: 600px) {
		display: inherit !important;
		padding: 10px 16px 12px;
	}

	&.loading {
		display: flex;
		align-items: center;
		justify-content: center;
	}
	:deep(.ios-spinner) {
		margin-left: 5px !important;
		margin-top: unset !important;
	}

	.login-heading {
		font-size: 70px;
		text-align: center;
		color: #ffffff;
		margin-bottom: 60px;
		margin-left: -22px;

		@media screen and (max-width: 600px) {
			font-size: 50px;
			margin-top: 20px;
			margin-bottom: 10px;
		}
	}

	.login-heading a,
	.login-heading :deep(svg) {
		cursor: pointer;
		outline: 0;
		width: 230px;
		color: var(--neutral-0);
	}

	.name-logo {
		display: unset;

		:deep(svg:not(.backdrop)) {
			width: unset;
		}
	}

	.welcome {
		font-family: Avenir-Book;
		font-size: 46px;
		color: #ffffff;
		margin-bottom: 20px;

		@media screen and (max-width: 580px) {
			font-size: 24px;
		}
	}

	.form-wrapper,
	.loading-container,
	.error-container {
		width: 100%;
		max-width: 584px;
		max-height: 836px;
		background: #ffffff;
		border-radius: 13px;
		padding: 24px 52px;
	}

	.form-wrapper {
		.back-button {
			height: 30px;
			font-size: 30px;
			color: $neutral-500;
			cursor: pointer;
			outline: 0;
		}

		@media screen and (max-width: 580px) {
			padding: 16px;
		}
	}

	.loading-container,
	.error-container {
		@include flex-column-center;
		justify-content: center;
		min-height: 500px;
		.pronto-icon {
			color: var(--red-200);
			font-size: 63px;
		}
		> h2 {
			font-size: 18px;
			margin: 40px 0 0 0;
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';
@include overrideScheme('dark') {
	.pronto-container,
	.invite-wrapper {
		background: var(--neutral-0);
	}

	.invite-wrapper {
		.error-container,
		.form-wrapper {
			background: var(--neutral-300);
		}

		.pronto-input {
			background: var(--neutral-100);
		}

		.invite-verification {
			.pronto-input {
				background: var(--neutral-100);
			}
		}
	}

	.login-heading a,
	.login-heading svg {
		color: var(--teal-300) !important;
	}

	.main-card {
		.pronto-btn {
			background: var(--neutral-100);
			box-shadow: var(--neutral-200);
			&:hover {
				background: var(--neutral-200) !important;
			}
		}
	}
}
</style>
